import apiClient from "@/services/api/apiClient"

export const INDEX_PATH = "/material-compliance/company-index"
export interface Company {
  id: string
  name: string
  website: string
  createdAt: Date
}
export interface DiscoveryReview {
  id: string
  companyId: string
  url: string
  notes: string
  actor: string
  createdAt: Date
  nextDue: Date
}
export interface SavedDocument {
  id: string
  companyId: string
  filename: string
  contentType: string
  sourceUrl: string | null
  sha256: string
  byteSize: number
  createdAt: Date
  verification: "not_verified"
  documentDate: null
  decision: "unreviewed" | "approved" | "rejected"
  needsAttention: boolean
  overdue: boolean
}
export interface RetrievalRecipe {
  id: string
  candidateId: string
  landingUrl: string
  documentUrl: string
  baselineSha256: string
}
export interface RetrievalCheck {
  createdAt?: Date
  id: string
  status: string
  observedSha256: string | null
  observedCandidateId: string | null
  errorCode: string | null
}
export interface SourceObservation {
  replayEligible?: boolean
  id: string
  filename: string
  sourceUrl: string | null
  productPage: string | null
  observedAt: Date | null
  createdAt: Date
  createdBy: string
}
export interface Page<T> {
  items: T[]
  nextCursor: string | null
}
const root = "/company-index"
export const catalog = {
  upload: async (
    companyId: string,
    file: File,
    sourceUrl: string,
    productPage: string,
    key: string,
  ) => {
    const form = new FormData()
    form.append("file", file)
    form.append("idempotency_key", key)
    form.append("source_url", sourceUrl)
    form.append("product_page", productPage)
    return (
      await apiClient.post<SavedDocument>(
        `${root}/companies/${companyId}/documents`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      )
    ).data
  },
  companies: async (query: string, cursor?: string) =>
    (
      await apiClient.get<Page<Company>>(`${root}/companies`, {
        params: { query, cursor },
      })
    ).data,
  discoveryReview: async (id: string) =>
    (
      await apiClient.get<DiscoveryReview | null>(
        `${root}/companies/${id}/discovery-review`,
      )
    ).data,
  recordDiscoveryReview: async (
    id: string,
    body: { url: string; notes: string; reviewed: true },
  ) =>
    (
      await apiClient.post<DiscoveryReview>(
        `${root}/companies/${id}/discovery-review`,
        body,
      )
    ).data,
  company: async (id: string) =>
    (await apiClient.get<Company>(`${root}/companies/${id}`)).data,
  create: async (body: { name: string; website: string }) =>
    (await apiClient.post<Company>(`${root}/companies`, body)).data,
  document: async (id: string) =>
    (await apiClient.get<SavedDocument>(`${root}/documents/${id}`)).data,
  documents: async (id: string, cursor?: string) =>
    (
      await apiClient.get<Page<SavedDocument>>(
        `${root}/companies/${id}/documents`,
        { params: { cursor } },
      )
    ).data,
  importDocument: async (
    id: string,
    supplierId: string,
    recipeId: string,
    reviewed: boolean,
  ) =>
    (
      await apiClient.post<{ id: string; processingStatus?: string }>(
        `${root}/documents/${id}/import`,
        {
          supplierId,
          recipeId,
          reviewed,
        },
      )
    ).data,
  recipes: async (id: string) =>
    (await apiClient.get<RetrievalRecipe[]>(`${root}/documents/${id}/recipes`))
      .data,
  createRecipe: async (id: string, sourceId: string) =>
    (
      await apiClient.post<RetrievalRecipe>(`${root}/documents/${id}/recipes`, {
        sourceId,
        reviewed: true,
      })
    ).data,
  checks: async (id: string) =>
    (await apiClient.get<RetrievalCheck[]>(`${root}/recipes/${id}/checks`))
      .data,
  observations: async (id: string, cursor?: string) =>
    (
      await apiClient.get<Page<SourceObservation>>(
        `${root}/documents/${id}/observations`,
        { params: { cursor } },
      )
    ).data,
  download: async (id: string) =>
    (await apiClient.get<{ url: string }>(`${root}/documents/${id}/download`))
      .data,
  preview: async (id: string) =>
    (
      await apiClient.get<Blob>(`${root}/documents/${id}/preview`, {
        responseType: "blob",
      })
    ).data,
}

export type DocumentKind =
  | "reach"
  | "rohs"
  | "conflict_minerals"
  | "material_declaration"
  | "conformity"
  | "certificate"
  | "other"
  | "unknown"
export interface CurationState {
  candidateId: string
  revision: number
  decision: "unreviewed" | "approved" | "rejected"
  kind: DocumentKind
  reason: string
  reviewedBy: string | null
  reviewedAt: Date | null
  suggestion: null | {
    kind: DocumentKind
    reason: string
    evidence: { page: number; quote: string }[]
    status: "suggested" | "needs_review"
    model: string | null
    promptVersion: string
    sha256: string
  }
  needsAttention: boolean
  overdue: boolean
  retrievalStatus: string
  schedule: null | {
    recipeId: string
    revision: number
    enabled: boolean
    nextDue: Date
    requested?: boolean
    running?: boolean
  }
  runs: {
    id: string
    status: string
    errorCode: string | null
    observedSha256: string | null
    observedCandidateId: string | null
    executor: string
    createdAt: Date
  }[]
}
export type RecheckBatch = {
  queued: number
  running: number
  skipped: number
  nextCursor: string | null
}
export const curation = {
  queueChecks: async (companyId?: string, cursor?: string) =>
    (
      await apiClient.post<RecheckBatch>(`${root}/rechecks`, null, {
        params: { company_id: companyId, cursor },
      })
    ).data,
  queueCheck: async (
    id: string,
    body?: { recipeId: string; expectedRevision: number; enabled: boolean },
  ) =>
    (
      await apiClient.post<CurationState>(
        `${root}/documents/${id}/recheck`,
        body,
      )
    ).data,
  state: async (id: string) =>
    (await apiClient.get<CurationState>(`${root}/documents/${id}/curation`))
      .data,
  history: async (id: string) =>
    (
      await apiClient.get<
        {
          revision: number
          actor: string
          createdAt: Date
          details: { action: string }
        }[]
      >(`${root}/documents/${id}/curation/history`)
    ).data,
  attention: async (companyId?: string, cursor?: string) =>
    (
      await apiClient.get<Page<SavedDocument>>(`${root}/attention`, {
        params: { company_id: companyId, cursor },
      })
    ).data,
  classify: async (id: string, expectedRevision: number) =>
    (
      await apiClient.post<CurationState>(`${root}/documents/${id}/classify`, {
        expectedRevision,
      })
    ).data,
  review: async (
    id: string,
    body: {
      expectedRevision: number
      decision: "approved" | "rejected"
      kind: DocumentKind
      reason: string
      reviewed: boolean
      sourceFailureAcknowledged?: boolean
    },
  ) =>
    (
      await apiClient.put<CurationState>(
        `${root}/documents/${id}/curation`,
        body,
      )
    ).data,
  monitor: async (
    id: string,
    body: { expectedRevision: number; recipeId: string; enabled: boolean },
  ) =>
    (
      await apiClient.put<CurationState>(
        `${root}/documents/${id}/monitor`,
        body,
      )
    ).data,
  check: async (
    id: string,
    body: { expectedRevision: number; recipeId: string; enabled: boolean },
  ) =>
    (
      await apiClient.post<CurationState>(
        `${root}/documents/${id}/monitor/check`,
        body,
      )
    ).data,
}
